import classNames from 'classnames';
import { useEffect, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Input from '@/components/molecules/input/input';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import Link from '@/components/utilities/link/link';
import useMutationAddPreBookingDiscountCode from '@/integrations/booking/pre-booking/discount-codes/add-discount-code/add-discount-code.hooks';
import useMutationDeletePreBookingDiscountCode from '@/integrations/booking/pre-booking/discount-codes/delete-discount-code/delete-discount-code.hooks';
import { QueryId } from '@/integrations/content/utils';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import styles from './booking-price-discount-modal.module.scss';

export interface BookingPriceDetailsModalProps {
  /** The already added discount code */
  addedDiscountCode?: string;
  /** The class name to apply */
  className?: string;
  /** The description text to display */
  description?: string;
  /** The headline text to display */
  headline?: string;
  /** The identifiers for the gift card query. */
  identifiers: {
    bookingStateId: string;
    id: string;
    pageId: string;
  };
  /** Indicates whether the modal is open. */
  isOpen?: boolean;
  /** The callback to handle the close event. */
  onClose?: (value: boolean) => void;
}

const BookingPriceDetailsModal = ({
  addedDiscountCode,
  className,
  description,
  headline,
  identifiers,
  isOpen = false,
  onClose,
}: BookingPriceDetailsModalProps) => {
  const { t } = useI18n();
  const { brand } = usePageDetailsContext();
  const [discountCode, setDiscountCode] = useState('');
  const [discountCodeError, setDiscountCodeError] = useState('');

  const invalidationKeys = [
    QueryId.GENERIC_PRODUCT,
    QueryId.MULTI_GENERIC_PRODUCT,
    QueryId.MULTI_SELECT_GENERIC_PRODUCT,
    QueryId.PARENT_PRODUCT,
    QueryId.PASSENGER_SUMMARY,
  ];

  const {
    data: dataAddDiscountCode,
    isLoading: isMutatingAddPreBookingDiscountCode,
    mutate: mutateAddPreBookingDiscountCode,
  } = useMutationAddPreBookingDiscountCode();

  const {
    data: dataDeleteDiscountCode,
    isLoading: isMutatingDeletePreBookingDiscountCode,
    mutate: mutateDeletePreBookingDiscountCode,
  } = useMutationDeletePreBookingDiscountCode();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountCode(event.target.value);
  };

  const handleSubmitDiscountCode = () => {
    if (!discountCode) return;

    mutateAddPreBookingDiscountCode({
      brand,
      discountCode,
      invalidationKeys: invalidationKeys,
      parameters: identifiers,
    });
  };

  const removeDiscountCode = (discountCode: string): void => {
    if (!discountCode) return;

    mutateDeletePreBookingDiscountCode({
      brand,
      discountCode: discountCode,
      invalidationKeys: invalidationKeys,
      parameters: identifiers,
    });
  };

  useEffect(() => {
    if (!dataAddDiscountCode) return;

    if (!dataAddDiscountCode.result) {
      setDiscountCodeError(
        dataAddDiscountCode.message
          ? dataAddDiscountCode.message
          : t('items.modules.bookingFlow.discountCodeError'),
      );
    } else {
      setDiscountCode('');
      setDiscountCodeError('');
      onClose?.(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAddDiscountCode]);

  useEffect(() => {
    if (!dataDeleteDiscountCode) return;

    if (!dataDeleteDiscountCode.result) {
      setDiscountCodeError(t('items.modules.bookingFlow.discountCodeError'));
    } else {
      setDiscountCode('');
      setDiscountCodeError('');
      onClose?.(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataDeleteDiscountCode]);

  return (
    <Lightbox
      className={classNames(styles.component, className)}
      isCentered
      isFullWidthOnMobile
      isOpen={isOpen}
      onClose={() => {
        onClose?.(false);
        setDiscountCodeError('');
      }}
      wrapperClassName={styles.lightboxWrapper}
    >
      <div className={styles.lightboxInnerWrapper}>
        <Typography tagStyle="headingMedium">{headline}</Typography>

        <Typography tagStyle="bodyMedium">{description}</Typography>

        <div className={styles.contentWrapper}>
          <div className={styles.inputWrapper}>
            <Input
              error={discountCodeError}
              hasClear
              label={t('items.modules.bookingFlow.discountCode')}
              onChange={handleInputChange}
              onClear={() => setDiscountCode('')}
              onEnter={handleSubmitDiscountCode}
              value={discountCode}
            />

            <Button
              className={styles.addButton}
              isLoading={
                isMutatingAddPreBookingDiscountCode ||
                isMutatingDeletePreBookingDiscountCode
              }
              onClick={handleSubmitDiscountCode}
              text={t('items.common.words.add')}
              variant="secondary"
            />
          </div>

          {addedDiscountCode && (
            <div className={styles.addedDiscountCodesWrapper}>
              <div className={styles.addedDiscountCode}>
                <Typography
                  className={styles.addedDiscountCodeText}
                  tagStyle="bodySmall"
                >
                  {`${addedDiscountCode} ${t('items.common.words.wasAdded')}`}
                </Typography>

                <Link
                  className={styles.removeDiscountCodeLink}
                  onClick={() => removeDiscountCode(addedDiscountCode)}
                >
                  <Typography tagStyle="bodySmall">
                    {t('items.common.words.remove')}
                  </Typography>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Lightbox>
  );
};

BookingPriceDetailsModal.displayName = 'BookingPriceDetailsModal';

export default BookingPriceDetailsModal;
